import { Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useT } from '../i18n/useT'

export interface RequireAuthProps {
  children: React.ReactNode
  requireAdmin?: boolean
}

/**
 * RequireAuth - protects routes that require authentication
 * Redirects to login if not authenticated
 */
export default function RequireAuth({ children, requireAdmin = false }: RequireAuthProps) {
  const { isAuthenticated, isLoading, user } = useAuth()
  const { t } = useT()

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          {t('Loading …')}
        </Typography>
      </Box>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Redirect to home if admin required but not admin
  if (requireAdmin && user?.role !== 'admin') {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
